import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BreedingListCreaturePopup } from '../breeding-list/breeding-list-creature.popup';
import { CreatureWebDto } from '../../../../../dtos/creature.web.dto';
import { CommonModule } from '@angular/common';
import { CreatureLiteWebDto } from '../../../../../dtos/creature.lite.web.dto';
import { ethers } from 'ethers';
import { Web3WalletService } from '../../../../../services/web3-wallet.service';
import { TranslateModule } from '@ngx-translate/core';
import { CreaturesService } from '../../../../../services/creatures.service';
import { firstValueFrom, forkJoin } from 'rxjs';

var window: Window & { ethereum: any } = globalThis as any;

@Component({
    selector: 'breeding-creature-popup',
    templateUrl: './breeding-creature.popup.html',
    styleUrl: './breeding-creature.popup.scss',
    imports: [
        CommonModule,
        BreedingListCreaturePopup,
        TranslateModule
    ]
})
export class BreedingCreaturePopup implements OnInit {
    private _show: boolean = false;
    @Input()
    get show(): boolean { return this._show; }
    set show(val: boolean) {
        if (val === this._show) return;
        this._show = val;
        if (val) {
            // Start in hidden state then transition
            this.animateIn = false;
            // Next tick to ensure initial styles applied
            setTimeout(() => this.animateIn = true, 0);
        } else {
            this.closeWithAnimation();
        }
    }
    @Output() showChange = new EventEmitter<boolean>();

    @Input()
    public creatureRef!: CreatureWebDto;
    public creatureAbilityToBreed: boolean = false;

    public selectedCreature: CreatureLiteWebDto | null = null;
    public selectedCreatureAbilityToBreed: boolean = false;

    public showBreedingListPopup: boolean = false;

    public animateIn: boolean = false;
    private animatingOut: boolean = false;

    constructor(private readonly web3WalletService: Web3WalletService, private readonly creaturesService: CreaturesService) {}

    async ngOnInit(): Promise<void> {
        this.creatureAbilityToBreed = await this.checkAbilityToBreed(this.creatureRef!.nftTokenId!);
    }

    closePopup() {
        // Public close intent (e.g., user clicks X)
        this.closeWithAnimation();
    }

    private closeWithAnimation() {
        if (this.animatingOut || !this._show) return;
        this.animatingOut = true;
        this.animateIn = false; // triggers exit animation
        // Match CSS transition duration (keep in sync with SCSS: 350ms + small buffer)
        const EXIT_MS = 380;
        setTimeout(() => {
            this._show = false;
            this.animatingOut = false;
            this.showChange.emit(false);
        }, EXIT_MS);
    }

    public breedingFee: string | null = null;
    public balanceInEth: string | null = null;
    public async onSelectCreature(creature: CreatureLiteWebDto | any) {
        this.selectedCreature = creature;
        this.showBreedingListPopup = false;
        this.selectedCreatureAbilityToBreed = await this.checkAbilityToBreed(creature.nftTokenId!);

        const signer = await this.web3WalletService.getSigner();
        const contract: any = await this.web3WalletService.getBreedingContract();
        const rarities: number[] = [
            this.creatureRef.rarity,
            this.selectedCreature!.rarity
        ];
        const getBreedingFeeByRarities = await contract.getFunction("getBreedingFeeByRarities");
        const fee = await getBreedingFeeByRarities.staticCall(rarities);
        this.breedingFee = ethers.formatUnits(fee, 'ether');

        const paymentTokenAddr = await contract.getFunction("getPaymentTokenAddr");
        const paymentTokenAddress = await paymentTokenAddr.staticCall();

        const paymentTokenContract: any = new ethers.Contract(paymentTokenAddress, [
            "function balanceOf(address account) public view returns (uint256)"
        ], signer);

        const myAddress = this.web3WalletService.getWalletAddress()!;
        const balance = await paymentTokenContract.balanceOf(myAddress);
        this.balanceInEth = ethers.formatUnits(balance, 'ether');

        if (parseFloat(this.balanceInEth ?? '0') > 1){
            this.balanceInEth = parseFloat(this.balanceInEth ?? '0').toFixed(0).toString();
        }
    }

    async checkAbilityToBreed(tokenId: number): Promise<boolean> {
        const contract: any = await this.web3WalletService.getBreedingContract();
        const result = await contract.checkAbilityToBreed(tokenId);
        return result;
    }

    public showLoading: boolean = false;
    // Flag to drive success fusion animation after breed transaction succeeds
    public successAnimating: boolean = false;
    // Duration (ms) of success animation before auto closing popup
    private readonly SUCCESS_ANIMATION_MS = 2200;
    public async onBreed() {

        this.showLoading = true;

        let logs = [];

        try {

            const myAddress = this.web3WalletService.getWalletAddress()!;
            logs.push('MyAddress: ' + myAddress);

            const creature1Id = this.creatureRef.nftTokenId!;
            logs.push('Creature1Id: ' + creature1Id);
            const creature2Id = this.selectedCreature!.nftTokenId!;
            logs.push('Creature2Id: ' + creature2Id);

            const nftContract: any = await this.web3WalletService.getNftContract();
            const ownerOfCreature1Address = await nftContract.ownerOf(creature1Id);
            logs.push('Owner of creature 1 Address: ' + ownerOfCreature1Address);
            const ownerOfCreature2Address = await nftContract.ownerOf(creature2Id);
            logs.push('Owner of creature 2 Address: ' + ownerOfCreature2Address);

            if (ownerOfCreature1Address.toLowerCase() !== myAddress.toLowerCase()) {
                throw new Error("You don't own creature 1");
            }

            if (ownerOfCreature2Address.toLowerCase() !== myAddress.toLowerCase()) {
                throw new Error("You don't own creature 2");
            }

            const signer = await this.web3WalletService.getSigner();
            const contract: any = await this.web3WalletService.getBreedingContract();

            const feeData = await this.web3WalletService.getFeeData();
            const gasPriceInWei = ethers.formatUnits(feeData.gasPrice!, 'wei');

            logs.push('Contract Address: ' + contract.target);

            const result = await contract.checkAbilityToBreed(creature1Id);
            logs.push('Check ability to breed creature 1: ' + result);

            const result2 = await contract.checkAbilityToBreed(creature2Id);
            logs.push('Check ability to breed creature 2: ' + result2);

            const getNotFungibleTokenAddr = await contract.getFunction("getNotFungibleTokenAddr");
            const notFungibleTokenAddress = await getNotFungibleTokenAddr.staticCall();
            logs.push('NotFungibleTokenAddress: ' + notFungibleTokenAddress);

            const paymentTokenAddr = await contract.getFunction("getPaymentTokenAddr");
            const paymentTokenAddress = await paymentTokenAddr.staticCall();
            logs.push('PaymentTokenAddress: ' + paymentTokenAddress);

            const rarities: number[] = [
                this.creatureRef.rarity,
                this.selectedCreature!.rarity
            ];
            const getBreedingFeeByRarities = await contract.getFunction("getBreedingFeeByRarities");
            const fee = await getBreedingFeeByRarities.staticCall(rarities);
            logs.push('Breeding Fee: ' + fee);

            const paymentTokenContract: any = new ethers.Contract(paymentTokenAddress, [
                "function approve(address spender, uint256 amount) public returns (bool)",
                "function allowance(address owner, address spender) public view returns (uint256)",
                "function balanceOf(address account) public view returns (uint256)"
            ], signer);
            const paymentTokenAllowance = await paymentTokenContract.allowance(myAddress, contract.target);

            logs.push('PaymentTokenContract: Payment token allowance: ' + paymentTokenAllowance.toString());
            if (paymentTokenAllowance < fee) {
                const paymentTokenApproveTx = await paymentTokenContract.approve(contract.target, fee);
                const paymentTokenApproveReceipt = await paymentTokenApproveTx.wait();
                const hash = paymentTokenApproveReceipt.hash;
                logs.push('PaymentTokenContract: Payment token approval transaction hash: ' + hash);
            } else {
                logs.push('PaymentTokenContract: Payment token already approved');
            }

            const isApproved = await nftContract.isApprovedForAll(myAddress, contract.target);
            if (!isApproved) {
                const estimatedGas = await nftContract.setApprovalForAll.estimateGas(
                    contract.target,
                    true,
                    {
                        from: myAddress
                    }
                );
                const approvalForAllTx = await nftContract.setApprovalForAll(contract.target, true, {
                    gasLimit: estimatedGas,
                    gasPrice: gasPriceInWei,
                });
                const approvalForAllReceipt = await approvalForAllTx.wait();
                const hash = approvalForAllReceipt.hash;
                logs.push('NftContract: setApprovalForAll transaction hash: ' + hash);
            } else {
                logs.push('NftContract: setApprovalForAll already approved');
            }

            const estimatedGas = await contract.createBreedingOrder.estimateGas(
                creature1Id,
                creature2Id,
                {
                    from: myAddress
                }
            );
            const createBreedingOrderTx = await contract.createBreedingOrder(creature1Id, creature2Id, {
                gasLimit: estimatedGas,
                gasPrice: gasPriceInWei,
            });
            const createBreedingOrderReceipt = await createBreedingOrderTx.wait();
            const createBreedingOrderHash = createBreedingOrderReceipt.hash;

            logs.push('Breeding Order transaction hash: ' + createBreedingOrderHash);

            // let orderId: number | undefined;
            // for (const log of createBreedingOrderReceipt.logs) {
            //     if (log.address.toLowerCase() !== contract.target.toLowerCase()) continue;
            //     try {
            //         const event = contract.interface.parseLog(log);
            //         if (event && event.name === "OrderIsCreated") {
            //             orderId = parseInt(event.args.orderId.toString());
            //             break;
            //         }
            //     } catch (e) {
            //         // not the right event, skip
            //     }
            // }

            // logs.push('Parsed orderId: ' + orderId);


            // const checkReadyToHatchFn = await contract.getFunction('checkReadyToHatch');
            // while (true) {
            //     const isReadyToHatch = await checkReadyToHatchFn.staticCall(orderId);
            //     logs.push('Check Ready To Hatch: ' + isReadyToHatch);
            //     if (isReadyToHatch) {
            //         break;
            //     }
            //     await new Promise(resolve => setTimeout(resolve, 5000));
            // }

            // const createCreatureEstimatedGas = await contract.createCreature.estimateGas(
            //     orderId,
            //     {
            //         from: myAddress
            //     }
            // );

            // const createCreatureTx = await contract.createCreature(orderId, {
            //     gasLimit: createCreatureEstimatedGas,
            //     gasPrice: gasPriceInWei,
            // });
            // const createCreatureReceipt = await createCreatureTx.wait();
            // const createCreatureHash = createCreatureReceipt.hash;

            // logs.push('Breeding Create Creature transaction hash: ' + createCreatureHash);

            const task1 = this.creaturesService.сheckAbilityToBreedById(this.creatureRef.id);
            const task2 = this.creaturesService.сheckAbilityToBreedById(this.selectedCreature!.id);

            const [res1, res2] = await firstValueFrom(forkJoin([task1, task2]));

            this.creatureRef.isAllowForBreeding = res1.isAllowForBreeding;
            this.creatureRef.breedingCount = res1.breedingCount;
            this.creatureRef.breedingParents = res1.breedingParents;
            this.creatureRef.breedingAbilities = res1.breedingAbilities;
            this.showLoading = false;
            // Trigger success animation instead of immediate close
            this.successAnimating = true;
            // Auto close after animation completes
            setTimeout(() => {
                this.successAnimating = false;
                this.closePopup();
            }, this.SUCCESS_ANIMATION_MS);

        } catch (error) {
            logs.push('Error: ' + JSON.stringify(error));
            console.error('Error executing breeding function:', error);
            this.showLoading = false;
        } finally {

        }
    }
}
